import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { setCachedProfileDisplay } from '../../services/profileDisplayCache';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { userApiClient } from '../../userApiClient';
import { UserProfile } from '../../models/Profile';
import { NotificationSnackbar, SnackbarSeverity } from '../../components/common/NotificationSnackbar';
import TutorPublicVisibilityToggle from '../../components/tutor/TutorPublicVisibilityToggle';
import TutorSessionTypeChoiceList from '../../components/tutor/TutorSessionTypeChoiceList';
import { tutorApiClient } from '../../tutorApiClient';
import { TutorLessonOption, TutorSessionType, TutorSkill, HostTutorResponse } from '../../models/Tutor';
import {
  buildUpdateTutorProfilePayload,
  extractTutorProfileValidationMessage,
  normalizeStudentLessonCities,
  normalizeTutorSessionTypes,
  supportsAtStudentPlace,
  supportsOfflineSession,
} from '../../utils/tutorSessionType';
import {
  createEmptyPlaceAddressDraft,
  placeAddressDraftFromRecord,
  placeAddressRecordFromTutorSpaceAddress,
  validateOfflineSessionPlaceAddress,
  type PlaceAddressDraft,
  type PlaceAddressValidationFieldErrors,
} from '../../utils/placeAddress';
import {
  createDefaultTutorWorkingHoursDraft,
  getTutorWorkingHoursFieldErrors,
  hasTutorWorkingHoursErrors,
  isValidTutorTimezone,
  resolveTutorTimezoneOrDefault,
  TUTOR_WORKING_HOURS_SECTION_ID,
  tutorWorkingHoursDraftFromApi,
  type TutorWorkingHoursDraft,
  type TutorWorkingHoursFieldErrors,
} from '../../utils/tutorWorkingHours';
import { getLanguageOptions, resolveLanguageLabel, type LanguageOption } from '../../utils/languageOptions';
import TutorWorkingHoursEditor from '../../components/tutor/TutorWorkingHoursEditor';
import TutorSkillAutocomplete from '../../components/tutor/TutorSkillAutocomplete';
import StudentLessonCitiesAutocomplete from '../../components/tutor/StudentLessonCitiesAutocomplete';
import TutorGoogleCalendarSection, {
  ORG_GOOGLE_CALENDAR_NOT_CONNECTED,
  persistGoogleCalendarSelection,
} from '../../components/tutor/TutorGoogleCalendarSection';
import { GoogleCalendarOption } from '../../models/GoogleCalendar';
import PlaceLocationAddressFields from '../../components/address/PlaceLocationAddressFields';
import PlaceAddressReadOnlyFields from '../../components/address/PlaceAddressReadOnlyFields';
import TutorBusinessLessonLocationSelect from '../../components/tutor/TutorBusinessLessonLocationSelect';
import { ensureLessonLocationSpaceId, isBusinessTutorOrganization } from '../../utils/tutorLessonLocation';
import { hostApiClient } from '../../hostApiClient';
import { OrganizationType } from '../../models/Organization';
import { Space } from '../../models/Space';
import { placeAddressRecordFromSpace } from '../../utils/placeAddress';
import { ProfilePhotoPicker } from '../../components/ProfilePhotoPicker';
import MediaUploader from '../../components/MediaUploader';
import { apiClient, HOST_RETENTION_EVENTS } from '../../publicApiClient';
import { scrollToElementByIdWithRetries } from '../../utils/scrollToElement';
import TutorLessonOptionCard from '../../components/tutor/TutorLessonOptionCard';
import { GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED, ORGANIZATION_GOOGLE_CALENDAR_HREF } from '../../utils/organizationSections';

interface TutorProfileCompletionPageProps {
  embedded?: boolean;
  onProfileSaved?: () => void | Promise<void>;
}

const TutorProfileCompletionPage: React.FC<TutorProfileCompletionPageProps> = ({
  embedded = false,
  onProfileSaved,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { tutorId: hostRouteTutorId } = useParams<{ tutorId?: string }>();
  /** Host editing another org tutor’s listing from `/host/tutors/:tutorId`. */
  const isHostListingView = Boolean(hostRouteTutorId);
  const theme = useTheme();
  const isWideScheduleLayout = useMediaQuery(theme.breakpoints.up('md'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [name, setName] = useState('');
  const [headline, setHeadline] = useState('');
  const [overview, setOverview] = useState('');
  const [lessonOptions, setLessonOptions] = useState<TutorLessonOption[]>([]);
  const [skills, setSkills] = useState<TutorSkill[]>([]);
  /** ISO 639-1 lowercase codes, aligned with API (`languages: ["pl", "en"]`). */
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [initialProfileImageUrl, setInitialProfileImageUrl] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasTutorProfile, setHasTutorProfile] = useState(false);
  const [supportedSessionTypes, setSupportedSessionTypes] = useState<TutorSessionType[]>([]);
  const [studentLessonCities, setStudentLessonCities] = useState<string[]>([]);
  const [studentLessonCitiesShowValidation, setStudentLessonCitiesShowValidation] = useState(false);
  const [lessonLocationDraft, setLessonLocationDraft] = useState<PlaceAddressDraft>(createEmptyPlaceAddressDraft());
  const [lessonLocationErrors, setLessonLocationErrors] = useState<PlaceAddressValidationFieldErrors | null>(null);
  const [lessonLocationShowValidation, setLessonLocationShowValidation] = useState(false);
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [organizationType, setOrganizationType] = useState<OrganizationType | undefined>(undefined);
  const [orgSpaces, setOrgSpaces] = useState<Space[]>([]);
  const [orgSpacesLoading, setOrgSpacesLoading] = useState(false);
  const [orgSpacesError, setOrgSpacesError] = useState<string | null>(null);
  const [lessonLocationSpaceError, setLessonLocationSpaceError] = useState<string | null>(null);
  const [workingHoursDraft, setWorkingHoursDraft] = useState<TutorWorkingHoursDraft>(
    createDefaultTutorWorkingHoursDraft()
  );
  const [timezone, setTimezone] = useState(resolveTutorTimezoneOrDefault(null));
  const [skillsLanguagesShowValidation, setSkillsLanguagesShowValidation] = useState(false);
  const [nameShowValidation, setNameShowValidation] = useState(false);
  const [workingHoursErrors, setWorkingHoursErrors] = useState<TutorWorkingHoursFieldErrors | null>(null);
  const [workingHoursShowValidation, setWorkingHoursShowValidation] = useState(false);
  const [tutorId, setTutorId] = useState<string | null>(null);
  const [calendarId, setCalendarId] = useState<string | null>(null);
  const [googleCalendarSelection, setGoogleCalendarSelection] = useState<GoogleCalendarOption | null>(null);
  const [calendarTouched, setCalendarTouched] = useState(false);
  const [calendarOrgNotConnected, setCalendarOrgNotConnected] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>('success');
  const [visible, setVisible] = useState(true);

  const locked = saving;

  const applyHostTutorToState = useCallback((tutor: HostTutorResponse) => {
    setName(tutor.name?.trim() ?? '');
    setHeadline(tutor.headline?.trim() ?? '');
    setOverview(tutor.overview?.trim() ?? '');
    setLessonOptions(tutor.lessonOptions ?? []);
    setSkills(Array.from(new Set(tutor.skills ?? [])));
    setSelectedLanguages((tutor.languages ?? []).map((code) => code.trim().toLowerCase()).filter(Boolean));
    setSupportedSessionTypes(normalizeTutorSessionTypes(tutor.supportedSessionTypes));
    setStudentLessonCities(normalizeStudentLessonCities(tutor.studentLessonCities));
    setStudentLessonCitiesShowValidation(false);
    setLessonLocationErrors(null);
    setLessonLocationDraft(placeAddressDraftFromRecord(placeAddressRecordFromTutorSpaceAddress(tutor)));
    setSpaceId(tutor.spaceId ?? null);
    setWorkingHoursDraft(tutorWorkingHoursDraftFromApi(tutor.workingHours));
    setTimezone(resolveTutorTimezoneOrDefault(tutor.timezone));
    setWorkingHoursErrors(null);
    setTutorId(tutor.tutorId);
    setCalendarId(tutor.calendarId?.trim() ? tutor.calendarId : null);
    setVisible(tutor.visible !== false);
    const image = tutor.profileImageUrl?.trim() || '';
    setProfileImageUrl(image);
    setInitialProfileImageUrl(image);
    setImageUrls(Array.isArray(tutor.imageUrls) ? tutor.imageUrls.filter(Boolean) : []);
    setHasTutorProfile(true);
    setHasChanges(false);
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        if (isHostListingView && hostRouteTutorId) {
          const [tutors, organization, hostProfile] = await Promise.all([
            tutorApiClient.getMyTutors(),
            hostApiClient.getOrganization(),
            userApiClient.getProfile().catch(() => null),
          ]);
          const tutor = tutors.find((row) => row.tutorId === hostRouteTutorId);
          if (!tutor) {
            setError(t('hostMyTutors.tutorNotFound', 'Tutor not found on your roster.'));
            return;
          }
          setOrganizationType(organization.organizationType);
          if (hostProfile) {
            setProfile(hostProfile);
          }
          applyHostTutorToState(tutor);
          return;
        }

        const data = await userApiClient.getProfile();
        setProfile(data);
        setName(data.name || '');
        const userImg = data.profileImageUrl?.trim() || '';
        let tutorProfileLoaded = false;

        try {
          const tutorSettings = await tutorApiClient.getTutorProfileSettings();
          setHeadline(tutorSettings.headline?.trim() ?? '');
          setOverview(tutorSettings.overview?.trim() ?? '');
          setLessonOptions(tutorSettings.lessonOptions ?? []);
          setSkills(normalizeSkills(tutorSettings.skills ?? []));
          setSelectedLanguages(normalizeLanguages(tutorSettings.languages ?? []));
          setSupportedSessionTypes(normalizeTutorSessionTypes(tutorSettings.supportedSessionTypes));
          setStudentLessonCities(normalizeStudentLessonCities(tutorSettings.studentLessonCities));
          setStudentLessonCitiesShowValidation(false);
          setLessonLocationErrors(null);
          setLessonLocationDraft(
            placeAddressDraftFromRecord(placeAddressRecordFromTutorSpaceAddress(tutorSettings))
          );
          setSpaceId(tutorSettings.spaceId ?? null);
          setOrganizationType(tutorSettings.organizationType);
          setWorkingHoursDraft(tutorWorkingHoursDraftFromApi(tutorSettings.workingHours));
          setTimezone(resolveTutorTimezoneOrDefault(tutorSettings.timezone));
          setWorkingHoursErrors(null);
          setTutorId(tutorSettings.tutorId?.trim() ? tutorSettings.tutorId : null);
          setCalendarId(tutorSettings.calendarId?.trim() ? tutorSettings.calendarId : null);
          setVisible(tutorSettings.visible !== false);
          const displayed = tutorSettings.profileImageUrl?.trim() || userImg;
          setProfileImageUrl(displayed);
          setInitialProfileImageUrl(displayed);
          setImageUrls(
            Array.isArray(tutorSettings.imageUrls) ? tutorSettings.imageUrls.filter(Boolean) : []
          );
          setHasTutorProfile(true);
          tutorProfileLoaded = true;
        } catch (tutorError) {
          if (axios.isAxiosError(tutorError) && tutorError.response?.status === 404) {
            setHasTutorProfile(false);
            tutorProfileLoaded = false;
            setHeadline('');
            setOverview('');
            setLessonOptions([]);
            setSupportedSessionTypes([]);
            setStudentLessonCities([]);
            setStudentLessonCitiesShowValidation(false);
            setLessonLocationErrors(null);
            setLessonLocationDraft(createEmptyPlaceAddressDraft());
            setSpaceId(null);
            setOrganizationType(undefined);
            setWorkingHoursDraft(createDefaultTutorWorkingHoursDraft());
            setTimezone(resolveTutorTimezoneOrDefault(null));
            setWorkingHoursErrors(null);
            setTutorId(null);
            setCalendarId(null);
            setGoogleCalendarSelection(null);
            setCalendarTouched(false);
            setCalendarOrgNotConnected(false);
            setProfileImageUrl(userImg);
            setInitialProfileImageUrl(userImg);
            setImageUrls([]);
            setVisible(true);
          } else {
            throw tutorError;
          }
        }

        if (!tutorProfileLoaded && data.roles.includes('HOST')) {
          try {
            const organization = await hostApiClient.getOrganization();
            setOrganizationType(organization.organizationType);
          } catch {
            // Host org lookup is best-effort before the tutor listing exists.
          }
        }
      } catch (_e) {
        setError(t('profile.fetchError', 'Failed to fetch profile'));
      } finally {
        setLoading(false);
      }
    };

    void loadProfile();
    // Omit `t`: a new i18n `t` identity would refetch and wipe in-progress edits.
  }, [applyHostTutorToState, hostRouteTutorId, isHostListingView]);

  const uiLocale = i18n.language || 'en';
  const languageOptions = useMemo(() => getLanguageOptions(uiLocale), [uiLocale]);
  const isBusinessOrg = isBusinessTutorOrganization(organizationType);
  const isHost = isHostListingView || (profile?.roles ?? []).includes('HOST');
  /** Only org hosts can list/manage business spaces (GET /v1/host/spaces is host-scoped). */
  const canManageBusinessSpaces = isBusinessOrg && isHost;
  /** Invited tutors in a business org get a host-assigned, read-only lesson location. */
  const isHostManagedTutorLocation = isBusinessOrg && !isHost;

  const applyBusinessSpaceSelection = (nextSpaceId: string, spaces: Space[]) => {
    const selected = spaces.find((space) => space.id === nextSpaceId);
    setSpaceId(nextSpaceId);
    if (selected) {
      setLessonLocationDraft(placeAddressDraftFromRecord(placeAddressRecordFromSpace(selected)));
    }
    setLessonLocationSpaceError(null);
    setHasChanges(true);
  };

  useEffect(() => {
    if (!canManageBusinessSpaces) {
      setOrgSpaces([]);
      setOrgSpacesError(null);
      setOrgSpacesLoading(false);
      return;
    }

    let cancelled = false;
    const loadSpaces = async () => {
      setOrgSpacesLoading(true);
      setOrgSpacesError(null);
      try {
        const spaces = await hostApiClient.getSpaces();
        if (cancelled) {
          return;
        }
        setOrgSpaces(spaces);
        const initialSpaceId = spaceId;
        if (initialSpaceId && spaces.some((space) => space.id === initialSpaceId)) {
          const selected = spaces.find((space) => space.id === initialSpaceId);
          if (selected) {
            setLessonLocationDraft(placeAddressDraftFromRecord(placeAddressRecordFromSpace(selected)));
          }
        } else if (!initialSpaceId && spaces.length === 1) {
          setSpaceId(spaces[0].id);
          setLessonLocationDraft(placeAddressDraftFromRecord(placeAddressRecordFromSpace(spaces[0])));
        }
      } catch {
        if (!cancelled) {
          setOrgSpaces([]);
          setOrgSpacesError(
            t('tutor.profileCompletion.lessonLocationSpacesLoadError', 'Failed to load organization spaces.')
          );
        }
      } finally {
        if (!cancelled) {
          setOrgSpacesLoading(false);
        }
      }
    };

    void loadSpaces();
    return () => {
      cancelled = true;
    };
  }, [canManageBusinessSpaces]);

  useEffect(() => {
    if (loading) return;
    if (location.hash !== `#${TUTOR_WORKING_HOURS_SECTION_ID}`) return;
    return scrollToElementByIdWithRetries(TUTOR_WORKING_HOURS_SECTION_ID, {
      // Keep the Working hours card clear of the sticky dashboard app bar,
      // but still land on the editor (not stuck mid-page above it).
      offsetPx: 72,
      delaysMs: [80, 250, 600, 1200],
    });
  }, [loading, location.hash]);

  const selectedLanguageOptions: LanguageOption[] = useMemo(() => {
    const byCode = new Map(languageOptions.map((o) => [o.code, o]));
    return selectedLanguages
      .map((code) => {
        const known = byCode.get(code);
        if (known) return known;
        return {
          code,
          label: resolveLanguageLabel(code, i18n.language),
          section: 'all' as const,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, uiLocale.replace(/_/g, '-'), { sensitivity: 'base' }));
  }, [languageOptions, selectedLanguages, uiLocale]);

  const normalizeLanguages = (values: string[]): string[] => {
    const normalized = values
      .map((value) => value.trim().toLowerCase())
      .filter((value) => value.length > 0);
    return Array.from(new Set(normalized));
  };

  const normalizeSkills = (values: TutorSkill[]): TutorSkill[] => Array.from(new Set(values));

  const handleSupportedSessionTypesChange = (next: TutorSessionType[]) => {
    setSupportedSessionTypes(next);
    if (!supportsAtStudentPlace(next)) {
      setStudentLessonCities([]);
      setStudentLessonCitiesShowValidation(false);
    }
    setLessonLocationErrors(null);
    setHasChanges(true);
  };

  const handleGoogleCalendarSelectionChange = useCallback((
    next: GoogleCalendarOption | null,
    meta?: { restored?: boolean }
  ) => {
    setGoogleCalendarSelection(next);
    setCalendarOrgNotConnected(false);
    if (!meta?.restored) {
      setCalendarTouched(true);
      setHasChanges(true);
    }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setCalendarOrgNotConnected(false);
    setLessonLocationErrors(null);
    setLessonLocationShowValidation(false);
    setWorkingHoursErrors(null);
    setWorkingHoursShowValidation(false);
    setSkillsLanguagesShowValidation(false);
    setStudentLessonCitiesShowValidation(false);
    setNameShowValidation(false);
    try {
      if (!isHostListingView && !name.trim()) {
        setNameShowValidation(true);
        throw new Error(t('tutor.profileCompletion.nameRequired', 'Enter your name.'));
      }

      const normalizedSkills = normalizeSkills(skills);
      const normalizedLanguages = normalizeLanguages(selectedLanguages);
      if (normalizedSkills.length === 0 || normalizedLanguages.length === 0) {
        setSkillsLanguagesShowValidation(true);
        throw new Error(
          t(
            'tutor.profileCompletion.skillsLanguagesRequired',
            'Select at least one skill and one language.'
          )
        );
      }

      const normalizedSessionTypes = normalizeTutorSessionTypes(supportedSessionTypes);
      const normalizedStudentCities = supportsAtStudentPlace(normalizedSessionTypes)
        ? normalizeStudentLessonCities(studentLessonCities)
        : [];
      if (supportsAtStudentPlace(normalizedSessionTypes) && normalizedStudentCities.length === 0) {
        setStudentLessonCitiesShowValidation(true);
        throw new Error(
          t(
            'tutor.profileCompletion.studentLessonCitiesRequired',
            'Add at least one city where you visit students.'
          )
        );
      }

      const workingHoursValidation = getTutorWorkingHoursFieldErrors(workingHoursDraft, t);
      if (hasTutorWorkingHoursErrors(workingHoursValidation)) {
        setWorkingHoursShowValidation(true);
        setWorkingHoursErrors(workingHoursValidation);
        throw new Error(
          t('tutor.workingHours.fixErrors', 'Fix the highlighted working hours before saving.')
        );
      }

      if (timezone.trim() && !isValidTutorTimezone(timezone)) {
        setWorkingHoursShowValidation(true);
        throw new Error(
          t('tutor.workingHours.timezoneInvalid', 'Enter a valid IANA timezone, e.g. Europe/Warsaw')
        );
      }
      /**
       * Host listing edit (`/host/tutors/:tutorId`) must not call PUT /v1/user/profile —
       * that updates the logged-in host, so the tutor’s form name/photo would overwrite
       * (or wipe) the host’s user record. Tutor display name/image go through listing PUT.
       */
      if (!isHostListingView) {
        await userApiClient.updateProfile({
          name: name.trim(),
          // Always send current value ("" clears). Omitting used to wipe the photo on the API.
          profileImageUrl: profileImageUrl.trim(),
        });
      }

      const imageDirty = profileImageUrl.trim() !== initialProfileImageUrl.trim();

      let effectiveSpaceId = spaceId;
      let effectiveTutorId = tutorId?.trim() || undefined;
      if (!hasTutorProfile && !isHostListingView) {
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) {
          throw new Error('No access token');
        }
        await tutorApiClient.completeTutorProfile({
          accessToken,
          name: name.trim(),
        });
        setHasTutorProfile(true);
        const freshSettings = await tutorApiClient.getTutorProfileSettings();
        effectiveSpaceId = freshSettings.spaceId ?? null;
        effectiveTutorId = freshSettings.tutorId?.trim() || undefined;
        setSpaceId(effectiveSpaceId);
        setLessonLocationDraft(
          placeAddressDraftFromRecord(placeAddressRecordFromTutorSpaceAddress(freshSettings))
        );
        setTutorId(effectiveTutorId ?? null);
        setCalendarId(freshSettings.calendarId?.trim() ? freshSettings.calendarId : null);
      }

      if (calendarTouched && effectiveTutorId && GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED) {
        await hostApiClient.updateTutorCalendar(effectiveTutorId, {
          googleCalendarInfo: googleCalendarSelection,
        });
        persistGoogleCalendarSelection(effectiveTutorId, googleCalendarSelection);
        if (isHostListingView) {
          const tutors = await tutorApiClient.getMyTutors();
          const refreshed = tutors.find((row) => row.tutorId === effectiveTutorId);
          setCalendarId(refreshed?.calendarId?.trim() ? refreshed.calendarId : null);
        } else {
          const calendarSettings = await tutorApiClient.getTutorProfileSettings();
          setCalendarId(calendarSettings.calendarId?.trim() ? calendarSettings.calendarId : null);
        }
        setCalendarTouched(false);
      }

      if (supportsOfflineSession(normalizedSessionTypes)) {
        if (isBusinessOrg) {
          if (!effectiveSpaceId?.trim()) {
            const message = isHostManagedTutorLocation
              ? t(
                  'tutor.profileCompletion.lessonLocationHostUnassigned',
                  'Your organization host has not assigned a lesson location yet. Ask them to set one before offering in-person lessons.'
                )
              : t(
                  'tutor.profileCompletion.lessonLocationSpaceRequired',
                  'Select a space for in-person lessons.'
                );
            setLessonLocationSpaceError(message);
            throw new Error(message);
          }
        } else {
          const locationValidation = validateOfflineSessionPlaceAddress(lessonLocationDraft, t);
          if (locationValidation) {
            setLessonLocationShowValidation(true);
            setLessonLocationErrors(locationValidation);
            throw new Error(
              t(
                'tutor.profileCompletion.lessonLocationRequiredForOffline',
                'Complete your lesson location before offering in-person lessons.'
              )
            );
          }

          effectiveSpaceId = await ensureLessonLocationSpaceId(
            effectiveSpaceId,
            lessonLocationDraft,
            t('tutor.profileCompletion.lessonLocationSpaceName', 'Lesson location')
          );
          setSpaceId(effectiveSpaceId);
        }
      }

      const listingPayload = buildUpdateTutorProfilePayload({
        spaceId: effectiveSpaceId,
        headline: headline.trim() || null,
        overview: overview.trim() || null,
        skills: normalizedSkills,
        languages: normalizedLanguages,
        profileImageUrl: profileImageUrl.trim(),
        imageUrls,
        supportedSessionTypes: normalizedSessionTypes,
        studentLessonCities: normalizedStudentCities,
        workingHoursDraft,
        timezone: resolveTutorTimezoneOrDefault(timezone),
        placeAddressDraft:
          supportsOfflineSession(normalizedSessionTypes) && !isBusinessOrg ? lessonLocationDraft : null,
        visible,
      });

      /** Full listing replace via tutor self or host org-scoped endpoint. */
      if (isHostListingView && hostRouteTutorId) {
        await tutorApiClient.updateHostTutorListing(hostRouteTutorId, listingPayload);
      } else {
        await tutorApiClient.updateTutorProfileSettings(listingPayload);
      }
      void apiClient.trackHostRetentionEvent(HOST_RETENTION_EVENTS.TUTOR_OFFER_SAVED, {
        userName: name.trim(),
        tutorId,
        spaceId: effectiveSpaceId,
        organizationType,
        lessonOptionsCount: lessonOptions.length,
        sessionTypes: normalizedSessionTypes,
        visible,
      });
      await onProfileSaved?.();
      setSupportedSessionTypes(normalizedSessionTypes);
      setStudentLessonCities(normalizedStudentCities);
      setInitialProfileImageUrl(profileImageUrl.trim());
      setCachedProfileDisplay({
        name: name.trim(),
        profileImageUrl: profileImageUrl.trim() || null,
      });
      window.dispatchEvent(new Event('profile-updated'));
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              name: name.trim(),
              ...(imageDirty
                ? { profileImageUrl: profileImageUrl.trim() ? profileImageUrl.trim() : undefined }
                : {}),
            }
          : prev
      );
      setSkills(normalizedSkills);
      setSelectedLanguages(normalizedLanguages);
      setWorkingHoursDraft(tutorWorkingHoursDraftFromApi(listingPayload.workingHours));
      setTimezone(resolveTutorTimezoneOrDefault(listingPayload.timezone));
      setHasChanges(false);
      setSnackbarMessage(t('common.changesSaved', 'Changes saved successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (saveError) {
      const apiMessage = extractTutorProfileValidationMessage(saveError);
      if (apiMessage === ORG_GOOGLE_CALENDAR_NOT_CONNECTED) {
        setCalendarOrgNotConnected(true);
        setError(
          t(
            'tutor.googleCalendar.orgNotConnected',
            'Your organization has not connected Google Calendar yet. Ask an admin to enable it under Integrations → Google Calendar.'
          )
        );
      } else {
        const clientMessage =
          saveError instanceof Error && saveError.message.trim() ? saveError.message.trim() : null;
        setError(apiMessage ?? clientMessage ?? t('profile.updateError', 'Failed to update profile'));
      }
      setSnackbarMessage(t('common.saveError', 'Failed to save changes'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container
      maxWidth={embedded ? false : 'lg'}
      disableGutters={embedded}
      sx={{ py: embedded ? 0 : 3 }}
    >
      {isHostListingView ? (
        <Chip
          label={`← ${t('hostMyTutors.backToRoster', 'Back to My Tutors')}`}
          onClick={() => navigate('/host/tutors')}
          sx={{
            mb: 2,
            bgcolor: 'transparent',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'transparent' },
          }}
        />
      ) : null}

      <Card
        sx={{
          p: { xs: 2, md: 3 },
          ...(embedded
            ? {
                border: 0,
                borderRadius: 0,
                boxShadow: 'none',
              }
            : {}),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'flex-start' },
            gap: { xs: 2, md: 3 },
            mb: 3,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              {isHostListingView
                ? t('hostMyTutors.listingEditTitle', 'Edit tutor listing')
                : t('tutor.profileCompletion.title')}
            </Typography>
            <Typography color="text.secondary">
              {isHostListingView
                ? t(
                    'hostMyTutors.listingEditSubtitle',
                    'Update this tutor’s public listing for your school — lesson options, skills, hours, and visibility.'
                  )
                : t('tutor.profileCompletion.subtitle')}
            </Typography>
          </Box>

          {hasTutorProfile ? (
            <TutorPublicVisibilityToggle
              visible={visible}
              disabled={saving}
              onChange={(checked) => {
                setVisible(checked);
                setHasChanges(true);
              }}
              label={t('tutor.profileCompletion.publicVisibilityLabel', 'Public search visibility')}
              onHint={t(
                'tutor.profileCompletion.publicVisibilityOnHint',
                'Your listing appears in public tutor search and city discovery.'
              )}
              offHint={t(
                'tutor.profileCompletion.publicVisibilityOffHint',
                'You stay on your organization roster, but students cannot find you in public search.'
              )}
              textAlign="right"
              sx={{ width: { xs: '100%', md: 'auto' }, maxWidth: { md: 380 }, flexShrink: 0 }}
            />
          ) : null}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            {calendarOrgNotConnected && profile?.roles.includes('HOST') ? (
              <Box sx={{ mt: 1 }}>
                <Typography
                  component="a"
                  href={ORGANIZATION_GOOGLE_CALENDAR_HREF}
                  variant="body2"
                  sx={{ color: 'inherit', fontWeight: 500 }}
                >
                  {t('tutor.googleCalendar.goToOrganizationSettings', 'Go to Google Calendar settings')} →
                </Typography>
              </Box>
            ) : null}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' },
            gap: { xs: 2.5, md: 3 },
            alignItems: 'start',
            mb: 3,
            pb: 3,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <ProfilePhotoPicker
            layout="start"
            value={profileImageUrl || undefined}
            onUrlChange={(url) => {
              setProfileImageUrl(url ?? '');
              setHasChanges(true);
            }}
            onError={(message) => {
              setSnackbarMessage(message);
              setSnackbarSeverity('error');
              setSnackbarOpen(true);
            }}
            disabled={locked}
          />

          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              label={t('profile.name', 'Name')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameShowValidation(false);
                setHasChanges(true);
              }}
              disabled={locked || isHostListingView}
              required={!isHostListingView}
              error={nameShowValidation && !name.trim()}
              helperText={
                nameShowValidation && !name.trim()
                  ? t('tutor.profileCompletion.nameRequired', 'Enter your name.')
                  : undefined
              }
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              <TutorSkillAutocomplete
                multiple
                id="tutor-profile-skills"
                value={skills}
                onChange={(next) => {
                  setSkills(next);
                  setSkillsLanguagesShowValidation(false);
                  setHasChanges(true);
                }}
                disabled={locked}
                required
                error={skillsLanguagesShowValidation && skills.length === 0}
                helperText={
                  skillsLanguagesShowValidation && skills.length === 0
                    ? t('tutor.profileCompletion.skillsRequired', 'Select at least one skill.')
                    : undefined
                }
                limitTags={4}
                sx={{ flex: { sm: '1 1 0' }, minWidth: 0 }}
              />

              <Autocomplete
                multiple
                fullWidth
                disabled={locked}
                id="tutor-profile-languages"
                options={languageOptions}
                groupBy={(option) => option.section}
                disableCloseOnSelect
                blurOnSelect={false}
                value={selectedLanguageOptions}
                onChange={(_, newValue) => {
                  setSelectedLanguages(newValue.map((o) => o.code));
                  setSkillsLanguagesShowValidation(false);
                  setHasChanges(true);
                }}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                getOptionLabel={(option) => option.label}
                limitTags={4}
                getLimitTagsText={(more) => t('tutor.profileCompletion.languagesMoreCount', { count: more })}
                ListboxProps={{
                  sx: {
                    maxHeight: 320,
                    p: 0,
                    overflow: 'auto',
                    '& .MuiAutocomplete-groupUl': { padding: 0 },
                  },
                }}
                renderGroup={(params) => {
                  const isPopular = params.group === 'popular';
                  return (
                    <li key={params.key} style={{ width: '100%', listStyle: 'none' }}>
                      {isPopular ? (
                        <Box
                          sx={{
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.04)
                                : alpha(theme.palette.primary.main, 0.05),
                            borderBottom: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Box
                            component="div"
                            role="presentation"
                            sx={{ px: 2, pt: 1.25, pb: 0.5, color: 'text.secondary' }}
                          >
                            <Typography
                              variant="overline"
                              sx={{ lineHeight: 1.5, display: 'block', letterSpacing: 0.8, fontWeight: 600 }}
                            >
                              {t('tutor.profileCompletion.languagesSectionPopular')}
                            </Typography>
                          </Box>
                          <Box
                            component="ul"
                            sx={{
                              m: 0,
                              p: 0,
                              px: 1.5,
                              pt: 0.5,
                              pb: 1.5,
                              listStyle: 'none',
                              width: '100%',
                              boxSizing: 'border-box',
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(3, minmax(0, 1fr))',
                              },
                              gap: 1,
                            }}
                          >
                            {params.children}
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Box
                            component="div"
                            role="presentation"
                            sx={{
                              px: 2,
                              py: 0.75,
                              bgcolor: 'action.hover',
                              color: 'text.secondary',
                              borderBottom: 1,
                              borderColor: 'divider',
                              position: 'static',
                            }}
                          >
                            <Typography variant="overline" sx={{ lineHeight: 1.5, display: 'block', letterSpacing: 1 }}>
                              {t('tutor.profileCompletion.languagesSectionAll')}
                            </Typography>
                          </Box>
                          <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none', width: '100%' }}>
                            {params.children}
                          </Box>
                        </>
                      )}
                    </li>
                  );
                }}
                renderOption={(props, option, { selected }) => {
                  const isPopular = option.section === 'popular';
                  return (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: isPopular ? 0.75 : 1,
                        minWidth: 0,
                        ...(isPopular
                          ? {
                              width: '100%',
                              minHeight: 44,
                              px: 1,
                              py: 0.75,
                              boxSizing: 'border-box',
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: selected ? 'primary.main' : 'divider',
                              bgcolor: selected ? 'action.selected' : 'background.paper',
                              transition: (theme) =>
                                theme.transitions.create(['background-color', 'border-color'], {
                                  duration: theme.transitions.duration.shorter,
                                }),
                              '&:hover': {
                                bgcolor: selected ? 'action.selected' : 'action.hover',
                                borderColor: selected ? 'primary.main' : 'text.disabled',
                              },
                              '&.Mui-focused': {
                                boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.24)}`,
                              },
                            }
                          : {
                              width: '100%',
                              '&.MuiAutocomplete-option': { alignItems: 'center' },
                            }),
                      }}
                    >
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        sx={{ p: isPopular ? 0.25 : 0.5, flexShrink: 0 }}
                        checked={selected}
                        tabIndex={-1}
                        disableRipple
                      />
                      <Typography
                        component="span"
                        variant="body2"
                        noWrap
                        title={option.label}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontWeight: isPopular && selected ? 600 : 400,
                        }}
                      >
                        {option.label}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    error={skillsLanguagesShowValidation && selectedLanguages.length === 0}
                    helperText={
                      skillsLanguagesShowValidation && selectedLanguages.length === 0
                        ? t(
                            'tutor.profileCompletion.languagesRequired',
                            'Select at least one language.'
                          )
                        : undefined
                    }
                    label={t('tutor.profileCompletion.languagesLabel')}
                    placeholder={t('tutor.profileCompletion.languagesPlaceholder')}
                  />
                )}
                sx={{ flex: { sm: '1 1 0' }, minWidth: 0 }}
              />
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 3,
            mb: 3,
            alignItems: 'stretch',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(0, 2fr)' },
          }}
        >
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label={t('tutor.profileCompletion.headline', 'Headline')}
                placeholder={t(
                  'tutor.profileCompletion.headlinePlaceholder',
                  'Short line that appears in search (e.g. style, level, specialty)'
                )}
                value={headline}
                onChange={(e) => {
                  setHeadline(e.target.value);
                  setHasChanges(true);
                }}
                disabled={locked}
                inputProps={{ maxLength: 200 }}
              />

              <TextField
                fullWidth
                multiline
                minRows={3}
                label={t('tutor.profileCompletion.overview', 'Overview')}
                placeholder={t(
                  'tutor.profileCompletion.overviewPlaceholder',
                  'Tell students about your experience, teaching style, and what to expect in lessons.'
                )}
                value={overview}
                onChange={(e) => {
                  setOverview(e.target.value);
                  setHasChanges(true);
                }}
                disabled={locked}
              />
            </Stack>
          </Card>

          <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <FormControl component="fieldset" variant="standard" fullWidth>
              <FormLabel component="legend" sx={{ mb: 1 }}>
                {t('tutor.profileCompletion.supportedSessionTypes', 'Session types')}
              </FormLabel>
              <TutorSessionTypeChoiceList
                selectedTypes={supportedSessionTypes}
                onChanged={handleSupportedSessionTypesChange}
                multiSelect
                enabled={!locked}
                aria-label={t('tutor.profileCompletion.supportedSessionTypes', 'Session types')}
              />
            </FormControl>

            {supportsAtStudentPlace(supportedSessionTypes) ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('tutor.profileCompletion.studentLessonCitiesTitle', 'Cities where you visit students')}
                </Typography>
                <StudentLessonCitiesAutocomplete
                  value={studentLessonCities}
                  onChange={(cities) => {
                    setStudentLessonCities(cities);
                    setStudentLessonCitiesShowValidation(false);
                    setHasChanges(true);
                  }}
                  disabled={locked}
                  error={
                    studentLessonCitiesShowValidation &&
                    normalizeStudentLessonCities(studentLessonCities).length === 0
                  }
                  helperText={
                    studentLessonCitiesShowValidation &&
                    normalizeStudentLessonCities(studentLessonCities).length === 0
                      ? t(
                          'tutor.profileCompletion.studentLessonCitiesRequired',
                          'Add at least one city where you visit students.'
                        )
                      : undefined
                  }
                />
              </Box>
            ) : null}

            {supportsOfflineSession(supportedSessionTypes) ? (
              <Box sx={{ mt: 2 }}>
                {canManageBusinessSpaces ? (
                  <TutorBusinessLessonLocationSelect
                    spaces={orgSpaces}
                    loading={orgSpacesLoading}
                    loadError={orgSpacesError}
                    selectedSpaceId={spaceId}
                    onSpaceChange={(nextSpaceId) => applyBusinessSpaceSelection(nextSpaceId, orgSpaces)}
                    selectionError={lessonLocationSpaceError}
                    readOnly={false}
                  />
                ) : isHostManagedTutorLocation ? (
                  <>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
                      <StorefrontOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="subtitle2">
                        {t('tutor.profileCompletion.lessonLocationTitle', 'Lesson location')}
                      </Typography>
                    </Stack>
                    {spaceId?.trim() ? (
                      <>
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                          {t(
                            'tutor.profileCompletion.lessonLocationHostManaged',
                            'Your in-person lesson location is managed by your organization host.'
                          )}
                        </Alert>
                        <PlaceAddressReadOnlyFields value={lessonLocationDraft} />
                      </>
                    ) : (
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        {lessonLocationSpaceError ??
                          t(
                            'tutor.profileCompletion.lessonLocationHostUnassigned',
                            'Your organization host has not assigned a lesson location yet. Ask them to set one before offering in-person lessons.'
                          )}
                      </Alert>
                    )}
                  </>
                ) : (
                  <>
                    {!spaceId?.trim() ? (
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        {t(
                          'tutor.profileCompletion.lessonLocationNoSpaceYet',
                          'Add your lesson address below. On save we link or create your lesson location space automatically.'
                        )}
                      </Alert>
                    ) : null}
                    <PlaceLocationAddressFields
                      value={lessonLocationDraft}
                      onChange={(next) => {
                        setLessonLocationDraft(next);
                        setLessonLocationErrors(null);
                        setHasChanges(true);
                      }}
                      showValidation={lessonLocationShowValidation}
                      fieldErrors={lessonLocationErrors}
                      title={t('tutor.profileCompletion.lessonLocationTitle', 'Lesson location')}
                    />
                  </>
                )}
              </Box>
            ) : null}
          </Card>
        </Box>

        <Card variant="outlined" sx={{ mb: 3, p: 1.5, borderRadius: 2 }}>
          <MediaUploader
            compact
            label={t('tutor.profileCompletion.portfolioGallery', 'Portfolio gallery')}
            initialMedia={imageUrls}
            onMediaChange={(media) => {
              setImageUrls(media);
              setHasChanges(true);
            }}
          />
        </Card>

        <Card variant="outlined" sx={{ mb: 3, p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
            <Typography variant="h6">
              {t('tutor.profileCompletion.lessonOptionsTitle', 'Lesson Options')}
            </Typography>
            {isHost ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate('/host/lesson-options')}
              >
                {t('tutor.profileCompletion.manageLessonOptions', 'Manage catalog')}
              </Button>
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isHost
              ? t(
                  'tutor.profileCompletion.lessonOptionsHostHint',
                  'Lesson options live in the host catalog. Assign this tutor from a lesson option’s detail page.'
                )
              : t(
                  'tutor.profileCompletion.lessonOptionsReadOnlyHint',
                  'Your host assigns lesson options to you. Students only see active assigned options.'
                )}
          </Typography>
          {lessonOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('hostMyTutors.noLessonOptions', 'No lesson options configured yet.')}
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 320px))',
              }}
            >
              {lessonOptions.map((option) => (
                <TutorLessonOptionCard
                  key={option.id ?? `${option.name}-${option.durationMinutes}-${option.price}`}
                  option={option}
                />
              ))}
            </Box>
          )}
        </Card>

        <Box
          id={TUTOR_WORKING_HOURS_SECTION_ID}
          sx={{
            display: 'grid',
            gap: 3,
            mb: 3,
            alignItems: 'stretch',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(0, 1fr)' },
            scrollMarginTop: 72,
          }}
        >
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {t('tutor.workingHours.title', 'Working hours')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(
                'tutor.workingHours.editorSubtitle',
                'Set the weekly hours when you accept lessons. These can be inherited from your space if left at defaults.'
              )}
            </Typography>
            <TutorWorkingHoursEditor
              value={workingHoursDraft}
              onChange={(next) => {
                setWorkingHoursDraft(next);
                setWorkingHoursErrors(null);
                setHasChanges(true);
              }}
              timezone={timezone}
              onTimezoneChange={(next) => {
                setTimezone(next);
                setHasChanges(true);
              }}
              fieldErrors={workingHoursErrors}
              showValidation={workingHoursShowValidation}
              disabled={locked}
              compact={isWideScheduleLayout}
            />
          </Card>

          <TutorGoogleCalendarSection
            hasTutorProfile={hasTutorProfile}
            tutorId={tutorId}
            calendarId={calendarId}
            value={googleCalendarSelection}
            onChange={handleGoogleCalendarSelectionChange}
            disabled={locked}
            compact={isWideScheduleLayout}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </Button>
        </Box>
      </Card>

      <NotificationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Container>
  );
};

export default TutorProfileCompletionPage;
